import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useSources } from '@/api/hooks.js';
import { ExperienceTabs } from '@/components/ExperienceTabs.js';
import { Card, EmptyState, ErrorNotice, Input, Loading } from '@/components/ui.js';
import { formatDateTime } from '@/lib/utils.js';

/**
 * Entry point of the Experience page: the projects the signed-in user can see.
 * The API narrows these to the ones they created, because the Kadanza endpoint
 * refuses an unscoped listing from an extension token.
 */
export function BrowsePage() {
  const [search, setSearch] = useState('');
  const sources = useSources(search);

  return (
    <div>
      <ExperienceTabs />

      <div className="mb-8 flex items-start justify-between gap-4">
        <p className="max-w-xl text-sm text-muted">
          Pick a project to send its layouts to an ad platform or DAM system.
        </p>
        <div className="w-64 shrink-0">
          <Input
            placeholder="Search by project name…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      </div>

      {sources.isLoading ? <Loading label="Loading projects…" /> : null}
      {sources.error ? <ErrorNotice error={sources.error} /> : null}

      {sources.data && sources.data.sources.length === 0 ? (
        <EmptyState
          title="No projects yet"
          description="Projects you create appear here, ready to send to an ad platform or DAM."
        />
      ) : null}

      <div className="grid gap-3">
        {sources.data?.sources.map((source) => {
          // The first layout is not necessarily the previewed one, so the card
          // shows whichever has rendered rather than an empty square.
          const thumbnail = source.layouts.find((layout) => layout.previewUrl)?.previewUrl;
          const digital = source.layouts.filter((layout) => layout.outputIntent !== 'print');
          const print = source.layouts.length - digital.length;
          // Only digital layouts can be sent, so only their progress is worth
          // reporting here.
          const rendering = digital.filter((layout) => !layout.contentUrl).length;
          const summary = [
            `${digital.length} digital`,
            print > 0 ? `${print} print` : null,
            rendering > 0 ? `${rendering} still rendering` : null,
            `updated ${formatDateTime(source.updatedAt)}`,
          ].filter(Boolean);

          return (
            <Card key={source.sourceId} className="p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  {thumbnail ? (
                    <img
                      src={thumbnail}
                      alt=""
                      className="h-14 w-14 rounded-brand border border-line object-cover"
                    />
                  ) : (
                    <div className="h-14 w-14 rounded-brand border border-dashed border-line" />
                  )}
                  <div>
                    <p className="font-medium">{source.name}</p>
                    <p className="text-sm text-muted">{summary.join(' · ')}</p>
                  </div>
                </div>
                <Link
                  to={`/experience/sources/${source.sourceId}`}
                  className="rounded-button bg-brand px-3 py-2 text-sm font-medium text-white hover:opacity-90"
                >
                  Distribute
                </Link>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
